import { AxiosResponse } from 'axios';
import {
  all,
  AllEffect,
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  SelectEffect,
  takeLatest,
} from 'redux-saga/effects';
import { Cart as CartConnector } from 'b2b-apps/shop/cart/api/Cart';
import {
  addCartToWishlistFulfilled,
  addCartToWishlistRejected,
  addItemsToCartFulfilled,
  addItemsToCartRejected,
  fetchCartFulfilled,
  fetchCartRejected,
  removeCartFulfilled,
  updateItemQuantityFulfilled,
  updateItemQuantityRejected,
} from 'b2b-apps/shop/cart/redux/cart/actionCreators';
import {
  ADD_CART_TO_WISHLIST,
  ADD_ITEMS_TO_CART,
  FETCH_CART,
  REMOVE_CART,
  REMOVE_VOUCHER,
  UPDATE_ITEM_QUANTITY,
  UPDATE_ITEM_REFERENCE,
  UPDATE_VOUCHER,
} from 'b2b-apps/shop/cart/redux/cart/actionTypes';
import {
  AddCartToWishlistAction,
  AddItemsToCartAction,
  FetchCartAction,
  RemoveCartAction,
  UpdateItemQuantityAction,
  UpdateItemReferenceAction,
  UpdateVoucherAction,
} from 'b2b-apps/shop/cart/redux/cart/types';
import { clearCartStatus, replaceCartStatus } from 'b2b-apps/shop/cart/redux/cartStatus/actionCreators';
import { getStatusFromCart } from 'b2b-apps/shop/cart/utils/cart';
import { requestApiSaga } from 'b2b-apps/shop/common/redux/api/sagas';
import { reportError } from 'b2b-apps/shop/common/redux/errors/actionCreators';
import { parseError } from 'b2b-apps/shop/common/utils/api/error';
import { AddItemsToCartType, CartProductType, CartType, WishlistWithAddedCartType } from 'b2b-common/core/cart/api/CartTypes';

function* addCartToWishlistSaga(action: AddCartToWishlistAction): Generator<
  CallEffect | PutEffect,
  void,
  AxiosResponse<WishlistWithAddedCartType>
> {
  const { name } = action.payload;

  try {
    const response = yield call(requestApiSaga, CartConnector.exportToWishlist(name));

    yield put(addCartToWishlistFulfilled(name, response.data));
  } catch (error) {
    yield put(addCartToWishlistRejected(name, parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* fetchCartSaga(action: FetchCartAction): Generator<
  CallEffect | PutEffect,
  void,
  AxiosResponse<CartType>
> {
  try {
    const response = yield call(requestApiSaga, CartConnector.fetch());
    const cart = response.data;

    yield put(fetchCartFulfilled(cart));
    yield put(replaceCartStatus(getStatusFromCart(cart)));
  } catch (error) {
    yield put(fetchCartRejected(parseError(error)));
    yield put(reportError(action.type, error));
  }
}

function* removeCartSaga(action: RemoveCartAction): Generator<CallEffect | PutEffect, void> {
  try {
    yield call(requestApiSaga, CartConnector.clear());
    yield put(removeCartFulfilled());
    yield put(clearCartStatus());
  } catch (error) {
    yield put(fetchCartRejected(parseError(error)));
    yield put(reportError(action.type, error));
  }
}

function* removeVoucherSaga(action: RemoveCartAction): Generator<
  CallEffect | PutEffect,
  void,
  AxiosResponse<CartType>
> {
  try {
    const response = yield call(requestApiSaga, CartConnector.clearVoucher());
    const cart = response.data;

    yield put(fetchCartFulfilled(cart));
    yield put(replaceCartStatus(getStatusFromCart(cart)));
  } catch (error) {
    yield put(fetchCartRejected(parseError(error)));
    yield put(reportError(action.type, error));
  }
}

function* updateItemQuantitySaga(action: UpdateItemQuantityAction): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  AxiosResponse<CartType> & CartProductType
> {
  const { quantity, sku } = action.payload;

  try {
    const response = yield call(
      requestApiSaga,
      CartConnector.updateItemQuantity(sku, quantity),
    );
    const cart = response.data;

    yield put(updateItemQuantityFulfilled(sku, quantity));
    yield put(fetchCartFulfilled(cart));
    yield put(replaceCartStatus(getStatusFromCart(cart)));
  } catch (error) {
    yield put(updateItemQuantityRejected(sku, quantity));
    yield put(fetchCartRejected(parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* updateItemReferenceSaga(action: UpdateItemReferenceAction): Generator<
  CallEffect | PutEffect,
  void,
  AxiosResponse<CartType>
> {
  const { reference, sku } = action.payload;

  try {
    const response = yield call(
      requestApiSaga,
      CartConnector.updateItemReference(sku, reference),
    );
    const cart = response.data;

    yield put(fetchCartFulfilled(cart));
    yield put(replaceCartStatus(getStatusFromCart(cart)));
  } catch (error) {
    yield put(fetchCartRejected(parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* updateVoucherSaga(action: UpdateVoucherAction): Generator<
  CallEffect | PutEffect,
  void,
  AxiosResponse<CartType>
> {
  const { code } = action.payload;

  try {
    const response = yield call(requestApiSaga, CartConnector.updateVoucher(code));
    const cart = response.data;

    yield put(fetchCartFulfilled(cart));
    yield put(replaceCartStatus(getStatusFromCart(cart)));
  } catch (error) {
    yield put(fetchCartRejected(parseError(error)));
    yield put(reportError(action.type, error));
  }
}

function* addItemsToCartSaga(action: AddItemsToCartAction): Generator<
  CallEffect | PutEffect,
  any,
  AxiosResponse<AddItemsToCartType>
> {
  const { items } = action.payload;

  try {
    const addItemsResponse = yield call(requestApiSaga, CartConnector.addItems(items));

    yield put(removeCartFulfilled());
    yield put(addItemsToCartFulfilled(addItemsResponse.data));
  } catch (error) {
    yield put(addItemsToCartRejected(parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* cartSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(ADD_CART_TO_WISHLIST, addCartToWishlistSaga),
    takeLatest(FETCH_CART, fetchCartSaga),
    takeLatest(REMOVE_CART, removeCartSaga),
    takeLatest(REMOVE_VOUCHER, removeVoucherSaga),
    takeLatest(UPDATE_ITEM_QUANTITY, updateItemQuantitySaga),
    takeLatest(UPDATE_ITEM_REFERENCE, updateItemReferenceSaga),
    takeLatest(UPDATE_VOUCHER, updateVoucherSaga),
    takeLatest(ADD_ITEMS_TO_CART, addItemsToCartSaga),
  ]);
}

export { cartSaga };
